import { ref } from "vue";
import { Service } from "@/apps/credit-line/services/network.service";

export interface ZipcodeAddressData {
  city: string;
  region: string;
  isZoneWithCoverage: boolean;
}

/**
 * Hook for zipcode-based address autocomplete (primarily for Mexico)
 * When a user enters a zipcode, this hook fetches the corresponding address data
 * from the backend and auto-populates city, region, and neighborhood fields.
 */
export function useZipcodeAutocomplete(deviceTag: string): {
  fetchAddressByZipcode: (
    zipcode: string
  ) => Promise<ZipcodeAddressData | null>;
  clearAddressData: () => void;
  isLoading: typeof isLoading;
  zipcodeError: typeof zipcodeError;
  addressData: typeof addressData;
} {
  const isLoading = ref(false);
  const zipcodeError = ref<string | null>(null);
  const addressData = ref<ZipcodeAddressData | null>(null);
  const CreditLineService = new Service();

  /**
   * Fetches address data based on zipcode
   * @param zipcode - The postal code to lookup
   * @returns Promise with address data or null if not found
   */
  const fetchAddressByZipcode = async (
    zipcode: string
  ): Promise<ZipcodeAddressData | null> => {
    if (!zipcode || zipcode.length < 5) {
      return null;
    }

    isLoading.value = true;
    zipcodeError.value = null;

    try {
      const response = await CreditLineService.validateCoverageZone({
        zipCode: zipcode,
        deviceTag,
      });

      const data: ZipcodeAddressData = {
        city: response.data.city || "",
        region: response.data.region || "",
        isZoneWithCoverage: response.data.isZoneWithCoverage,
      };

      addressData.value = data;

      if (!data.isZoneWithCoverage) {
        zipcodeError.value = "PAYJOY_CARD_VIEWS.ADDRESS_FORM_NO_COVERAGE";
      }
      return data;
    } catch (err) {
      zipcodeError.value = "PAYJOY_CARD_VIEWS.ADDRESS_FORM_NO_COVERAGE";
      console.error("Error in useZipcodeAutocomplete:", err);
      addressData.value = null;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Clears the current address data and error state
   */
  const clearAddressData = () => {
    addressData.value = null;
    zipcodeError.value = null;
  };

  return {
    fetchAddressByZipcode,
    clearAddressData,
    isLoading,
    zipcodeError,
    addressData,
  };
}
